import React from 'react'
import { Link } from '@tanstack/react-router'
import { PartnerService } from '@duck/api-definitions'
import { Button } from '@duck/ui'
import { getStatusBadgeColor, formatStatus, getTypeBadgeColor, formatType, type Column } from './utils'
import { OrganizationsTableSkeleton } from './OrganizationsTableSkeleton'
import { useTar } from '@/modules'
import { useLoadMoreOnScroll } from '@/hooks/useLoadMoreOnScroll'

interface OrganizationsTableProps {
    organizations: PartnerService.Organization[]
    totalOrganizations: number
    visibleColumns: Column[]
    sortField: keyof PartnerService.Organization
    sortDirection: 'asc' | 'desc'
    onSort: (field: keyof PartnerService.Organization) => void
    onOrganizationClick?: (organization: PartnerService.Organization) => void
    loadingMode?: boolean
    hasMore?: boolean
    onLoadMore?: () => void
    isPageLoading?: boolean
}

export const OrganizationsTable: React.FC<OrganizationsTableProps> = ({
    organizations,
    totalOrganizations,
    visibleColumns,
    sortField,
    sortDirection,
    onSort,
    onOrganizationClick,
    loadingMode = false,
    hasMore = false,
    onLoadMore,
    isPageLoading = false,
}) => {
    const { t } = useTar()

    const { sentinelRef: loadMoreSentinelRef, sentinelIndex: loadMoreSentinelIndex } = useLoadMoreOnScroll<HTMLTableRowElement>({
        hasMore,
        onLoadMore,
        isLoading: isPageLoading,
        disabled: loadingMode,
        itemCount: organizations.length,
        offsetFromEnd: 10,
    })

    const cellRenderers: Partial<Record<keyof PartnerService.Organization, (organization: PartnerService.Organization) => React.ReactNode>> = {
        id: (organization) => (
            <span className="text-xs font-mono text-muted-foreground">
                {organization.id}
            </span>
        ),
        name: (organization) => (
            <p className="text-sm font-medium text-foreground">
                {organization.name}
            </p>
        ),
        description: (organization) => (
            <p className="text-sm text-foreground max-w-md truncate">
                {organization.description || t('common.na')}
            </p>
        ),
        type: (organization) => (
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getTypeBadgeColor(organization.type)}`}>
                {t(formatType(organization.type))}
            </span>
        ),
        active: (organization) => (
            <span className={`inline-flex px-2 py-1 text-xs font-semibold rounded-full ${getStatusBadgeColor(organization.active)}`}>
                {t(formatStatus(organization.active))}
            </span>
        ),
        logoURL: (organization) => (
            organization.logoURL ? (
                <img src={organization.logoURL} alt={organization.name} className="h-8 w-8 object-contain" />
            ) : (
                <span className="text-xs text-muted-foreground italic">{t('common.na')}</span>
            )
        ),
        availableTaskIds: (organization) => (
            <span className="text-xs font-mono text-muted-foreground">
                {organization.availableTaskIds?.join(', ') || t('common.na')}
            </span>
        ),
    }

    const renderCellContent = (organization: PartnerService.Organization, columnKey: keyof PartnerService.Organization) => {
        const renderer = cellRenderers[columnKey]
        return renderer ? renderer(organization) : null
    }

    return (
        <div className="bg-card shadow overflow-hidden rounded-md sm:rounded-md border border-border">
            <div className="px-4 py-5 sm:px-6">
                <h3 className="text-lg leading-6 font-medium text-foreground">
                    {t('organizations.allOrganizations')}
                </h3>
                <p className="mt-1 max-w-2xl text-sm text-muted-foreground">
                    {loadingMode ? t('common.loading') : t('common.XRecords', { count: organizations.length })}
                </p>
            </div>

            <div className="overflow-x-auto overflow-y-hidden">
                <table className="min-w-full divide-y divide-border">
                    <thead className="bg-secondary/5">
                        <tr>
                            {visibleColumns.map((column) => (
                                <th
                                    key={column.key}
                                    className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider"
                                >
                                    {column.sortable ? (
                                        <button
                                            onClick={() => onSort(column.key as keyof PartnerService.Organization)}
                                            className="flex items-center gap-1 hover:text-foreground transition-colors"
                                        >
                                            <span>{t(column.label)}</span>
                                            {sortField === column.key && (
                                                <span className="text-accent">
                                                    {sortDirection === 'asc' ? '↑' : '↓'}
                                                </span>
                                            )}
                                        </button>
                                    ) : (
                                        t(column.label)
                                    )}
                                </th>
                            ))}
                            <th className="px-6 py-3 text-start text-xs font-medium text-muted-foreground uppercase tracking-wider">
                                {t('common.actions')}
                            </th>
                        </tr>
                    </thead>
                    <tbody className="bg-card divide-y divide-border">
                        {loadingMode ? (
                            <OrganizationsTableSkeleton visibleColumns={visibleColumns} rowCount={8} />
                        ) : (
                            organizations.map((organization, index) => {
                                const isLoadMoreSentinel = hasMore && !loadingMode && index === loadMoreSentinelIndex

                                return (
                                    <tr
                                        key={organization.id}
                                        ref={isLoadMoreSentinel ? loadMoreSentinelRef : undefined}
                                        className={`hover:bg-secondary/5 ${onOrganizationClick ? 'cursor-pointer' : ''}`}
                                        onClick={() => onOrganizationClick?.(organization)}
                                    >
                                        {visibleColumns.map((column) => (
                                            <td key={column.key} className="px-6 py-4 whitespace-nowrap">
                                                {renderCellContent(organization, column.key)}
                                            </td>
                                        ))}
                                        <td className="px-6 py-4 whitespace-nowrap" onClick={(e) => e.stopPropagation()}>
                                            <Link
                                                to="/admin/organizations/assignments/$organizationId"
                                                params={{ organizationId: organization.id }}
                                            >
                                                <Button variant="outline" size="sm">
                                                    {t('organizations.viewAssignments')}
                                                </Button>
                                            </Link>
                                        </td>
                                    </tr>
                                )
                            })
                        )}
                    </tbody>
                </table>
            </div>

            {hasMore && !loadingMode && (
                <div className="flex items-center justify-center py-4">
                    {isPageLoading && (
                        <div className="flex items-center justify-center">
                            <div className="animate-spin rounded-full h-6 w-6 border-b-2 border-accent"></div>
                            <span className="ms-2 text-sm text-muted-foreground">{t('common.loading')}</span>
                        </div>
                    )}
                </div>
            )}

            {!loadingMode && organizations.length === 0 && (
                <div className="text-center py-12">
                    <p className="text-sm text-muted-foreground">{t('organizations.noOrganizations')}</p>
                </div>
            )}
        </div>
    )
}
